import { Grid } from '@material-ui/core';
import MaterialTable, { Column } from 'material-table';
import React from 'react';
import update from 'react-addons-update';

import { IParam } from '~/src/dash/api/network';
import { IStore } from '~/src/dash/reducers/store';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';

interface ITableRow {
  required: boolean;
  name: string;
  value: string;
}

export interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  currentParams: IParam[];
  requiredParams: string[];

  // Params that shouldn't be edited
  notEditableParams: string[];
  onSave: (params: IParam[]) => void;
}

interface IState extends IBaseState {
  tableColumnsParams: Array<Column<ITableRow>>;
  tableData: ITableRow[];
}

function paramsToRow(params: IParam[], requiredParams: string[]): ITableRow[] {
  return params.map((data: IParam): ITableRow => {
    return {
      name: data.name,
      value: data.value,
      required: requiredParams.includes(data.name),
    };
  });
}

export const EndpointCustomTable = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
      section: {
        display: 'block',
        marginBottom: theme.spacing.unit * 4,
      },
      button: {
        margin: theme.spacing.unit,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      tableColumnsParams: [
        { title: 'Param', field: 'name', editable: 'never' as const },
        { title: 'Value', field: 'value', searchable: false, editable: 'always' as const },
        { title: 'Required', field: 'required', searchable: false, editable: 'never' as const },
      ],
      tableData: paramsToRow(this.props.currentParams, this.props.requiredParams),
    };

    componentDidUpdate(prevProps: Readonly<IProps>): void {
      if (prevProps.currentParams !== this.props.currentParams) {
        this.setState({
          tableData: paramsToRow(this.props.currentParams, this.props.requiredParams),
        });
      }
    }

    handleSave = () => {
      this.props.onSave(
        this.state.tableData.map((value: ITableRow): IParam => {
          return {
            name: value.name,
            value: value.value,
          };
        }),
      );
    };

    render() {
      return (
        <Grid container direction='column' justify='center' alignItems='center'>
          <Grid item>
            <MaterialTable
              style={{
                paddingLeft: '20px',
                paddingRight: '20px',
                width: '600px',
              }}
              title='Customize endpoint params value'
              options={{
                search: false,
                sorting: false,
                padding: 'dense',
                pageSize: 10,
                pageSizeOptions: [10, 20, 30],
              }}
              columns={this.state.tableColumnsParams}
              data={this.state.tableData}
              editable={{
                isEditable: (rowData: ITableRow) => {
                  return !this.props.notEditableParams.includes(rowData.name);
                },
                onRowAdd: undefined,
                onRowUpdate: (newData: ITableRow, oldData: ITableRow) => {
                  const indexOfOldData = this.state.tableData.indexOf(oldData as never);
                  return new Promise((resolve) => {
                    this.setState(
                      {
                        tableData: update(this.state.tableData, {
                          [indexOfOldData]: {
                            $set: newData,
                          },
                        }),
                      },
                      () => {
                        this.handleSave();
                        return resolve;
                      },
                    );
                  });
                },
                onRowDelete: undefined,
              }}
            />
          </Grid>
        </Grid>
      );
    }
  },
);
